import { Fragment, useEffect, useState } from "react";
import { api } from "../api";

const CATEGORY_LABEL = {
  scan: "Обследования",
  scanner: "Сканер",
  yclients: "YClients",
  sync: "Синхронизация",
  network: "Сеть",
  update: "Обновления",
  app: "Приложение",
};

const LEVEL_STYLE = {
  info: "text-slate-600 bg-slate-100",
  warn: "text-amber-700 bg-amber-50",
  error: "text-red-600 bg-red-50",
};

const LEVEL_LABEL = { info: "инфо", warn: "внимание", error: "ошибка" };
const PAGE_SIZE = 100;
const PENDING_LABEL = { documents: "обследований", patients: "пациентов", purchases: "покупок", files: "фото", payments: "оплат" };

function formatTime(value) {
  return value ? new Date(value).toLocaleString("ru-RU") : "—";
}

function formatShortTime(value) {
  return value ? new Date(value).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
}

function parseDetails(details) {
  try { return details ? JSON.parse(details) : null; } catch { return null; }
}

function formatDetails(details) {
  if (!details) return "";
  try { return JSON.stringify(JSON.parse(details), null, 2); } catch { return details; }
}

function Counter({ value, tone }) {
  if (!value) return <span className="text-slate-300">0</span>;
  return <span className={`font-semibold ${tone}`}>{value}</span>;
}

function LevelBadge({ level }) {
  return (
    <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${LEVEL_STYLE[level] || LEVEL_STYLE.info}`}>
      {LEVEL_LABEL[level] || level}
    </span>
  );
}

// What a point still hadn't delivered to central after its last sync (heartbeat, routes/sync.js).
function PendingCell({ pending }) {
  if (!pending) return <span className="text-slate-300">—</span>;
  const parts = Object.entries(PENDING_LABEL).filter(([key]) => pending[key] > 0).map(([key, label]) => `${pending[key]} ${label}`);
  if (!parts.length) return <span className="text-slate-300">0</span>;
  const total = Object.keys(PENDING_LABEL).reduce((n, key) => n + (pending[key] || 0), 0);
  const oldest = pending.oldestDocumentAt ? `; самое старое обследование — с ${formatTime(pending.oldestDocumentAt)}` : "";
  return <span className="font-semibold text-amber-700" title={`Не отправлено на центр: ${parts.join(", ")}${oldest}`}>{total}</span>;
}

// One scan (or patient) end to end: saved on the point → sent → accepted by central → YClients.
function TrailPanel({ id, onClose }) {
  const [trail, setTrail] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.getClientLogTrail(id).then(setTrail).catch((e) => setError(e.response?.data?.error || e.message));
  }, [id]);

  const title = trail?.document
    ? `Обследование ${trail.document.id.slice(0, 8)}`
    : trail?.patient ? `Пациент ${trail.patient.name}` : `История ${id.slice(0, 8)}`;

  return (
    <div className="fixed inset-0 bg-black/40 z-[200] flex items-center justify-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-[900px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            <div className="text-base font-bold text-slate-900">{title}</div>
            {trail && (
              <div className="text-xs text-slate-500 mt-1">
                {trail.patient && <>Пациент: {trail.patient.name}</>}
                {trail.document && <> · {trail.document.location || "—"} · создано {formatTime(trail.document.createdAt)}</>}
                {!trail.document && !trail.patient && <>На центре не найдено — показаны события точки по этому id</>}
              </div>
            )}
          </div>
          <button className="text-sm text-slate-500 hover:text-slate-800" onClick={onClose}>Закрыть</button>
        </div>
        <div className="overflow-y-auto px-5 py-3">
          {error ? <div className="text-sm text-red-700">{error}</div>
            : trail === null ? <div className="docs-empty">Загрузка...</div>
            : trail.items.length === 0 ? <div className="docs-empty">Событий нет</div>
            : (
              <table className="admin-table">
                <thead><tr><th>Когда</th><th>Где</th><th>Уровень</th><th>Событие</th></tr></thead>
                <tbody>
                  {trail.items.map((row) => (
                    <tr key={row.id} title={formatDetails(row.details)}>
                      <td className="whitespace-nowrap">{formatTime(row.first_at)}</td>
                      <td>{row.location === "central" ? "Центр" : row.location}</td>
                      <td><LevelBadge level={row.level} /></td>
                      <td className="text-slate-700">{row.message}{row.count > 1 ? <span className="text-xs text-slate-400"> ×{row.count}</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

// Diagnostic events uploaded by points (scans, scanner, YClients, sync, network, updates, app
// errors) — see backend/lib/clientLog.js and docs/LOGS.md. Central-admin only.
export default function PointLogsPage() {
  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [trailId, setTrailId] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ location: "", category: "", level: "problems", from: "", to: "", q: "" });

  const setFilter = (key, value) => { setItems(null); setOffset(0); setFilters((f) => ({ ...f, [key]: value })); };
  const goToOffset = (value) => { setItems(null); setOffset(value); };

  useEffect(() => {
    api.getClientLogSummary().then((r) => setSummary(r.items)).catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  useEffect(() => {
    const params = { limit: PAGE_SIZE, offset };
    for (const [key, value] of Object.entries(filters)) if (value) params[key] = value;
    if (params.to) params.to = `${params.to}T23:59:59`;
    const handle = setTimeout(() => {
      api.listClientLogs(params)
        .then((r) => { setItems(r.items); setTotal(r.total); })
        .catch((e) => { setError(e.response?.data?.error || e.message); setItems([]); });
    }, filters.q ? 300 : 0);
    return () => clearTimeout(handle);
  }, [filters, offset]);

  const selectClass = "h-8 text-xs rounded-md border border-slate-200 bg-white px-2";
  const trailButton = "text-xs text-blue-700 hover:underline";

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2 className="admin-title">Логи точек {total ? `(${total})` : ""}</h2>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <section className="rounded-xl border border-slate-200 bg-white p-4 mb-5">
        <h3 className="font-semibold text-slate-800 mb-3">Состояние точек за 24 часа</h3>
        <div className="overflow-x-auto">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Точка</th><th>Версия</th><th title="Компьютер точки (имя Windows · идентификатор установки)">Компьютер</th>
              <th title="Последняя синхронизация">Синк</th><th title="Когда пришли последние логи">Логи</th><th title="Последнее успешное сканирование">Скан</th>
              <th title="Обследований сохранено за сутки">Сохранено</th><th title="Сканов удалено и правок отброшено без сохранения за сутки">Без сохр.</th>
              <th title="Не отправлено на центр после последней синхронизации (наведите — подробности)">Не отпр.</th>
              <th>Ошибки</th><th title="Проблемы с сохранением и отправкой обследований">Обслед.</th><th>Сканер</th><th>YClients</th>
              <th title="Сеть и синхронизация">Сеть</th><th title="Сколько раз компьютер выходил из сна">Сон</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((row) => (
              <tr key={row.location} className="cursor-pointer" onClick={() => setFilter("location", row.location)}>
                <td className="font-medium">{row.location}</td>
                <td>{row.client_version ? `v${row.client_version}` : "—"}</td>
                <td className="whitespace-nowrap text-xs">
                  {row.machine || "—"}
                  {row.machines_24h > 1 && (
                    <span className="ml-1 font-semibold text-red-600" title="За сутки логи пришли с нескольких компьютеров — проверьте, не работают ли два компьютера под одним ключом точки">
                      ×{row.machines_24h}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap">{formatShortTime(row.last_seen_at)}</td>
                <td className="whitespace-nowrap">{formatShortTime(row.last_received_at)}</td>
                <td className="whitespace-nowrap">{formatShortTime(row.last_scan_at)}</td>
                <td><Counter value={row.scans_saved_24h} tone="text-emerald-700" /></td>
                <td><Counter value={row.scans_discarded_24h} tone="text-amber-700" /></td>
                <td><PendingCell pending={row.pending} /></td>
                <td><Counter value={row.errors_24h} tone="text-red-600" /></td>
                <td><Counter value={row.scan_problems_24h} tone="text-amber-700" /></td>
                <td><Counter value={row.scanner_problems_24h} tone="text-amber-700" /></td>
                <td><Counter value={row.yclients_problems_24h} tone="text-amber-700" /></td>
                <td><Counter value={row.sync_problems_24h} tone="text-amber-700" /></td>
                <td><Counter value={row.wakes_24h} tone="text-slate-700" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select className={selectClass} value={filters.location} onChange={(e) => setFilter("location", e.target.value)}>
          <option value="">Все точки</option>
          {summary.map((row) => <option key={row.location} value={row.location}>{row.location}</option>)}
        </select>
        <select className={selectClass} value={filters.category} onChange={(e) => setFilter("category", e.target.value)}>
          <option value="">Все категории</option>
          {Object.entries(CATEGORY_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select className={selectClass} value={filters.level} onChange={(e) => setFilter("level", e.target.value)}>
          <option value="problems">Только проблемы</option>
          <option value="error">Только ошибки</option>
          <option value="">Все события</option>
        </select>
        <input type="date" className={selectClass} value={filters.from} onChange={(e) => setFilter("from", e.target.value)} title="С даты" />
        <input type="date" className={selectClass} value={filters.to} onChange={(e) => setFilter("to", e.target.value)} title="По дату" />
        <input className={`${selectClass} w-56`} placeholder="Поиск по тексту или id" value={filters.q} onChange={(e) => setFilter("q", e.target.value)} />
      </div>

      {items === null ? (
        <div className="docs-empty">Загрузка...</div>
      ) : items.length === 0 ? (
        <div className="docs-empty">Событий нет</div>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr><th>Когда</th><th>Точка</th><th>Категория</th><th>Уровень</th><th>Событие</th><th>Повторов</th></tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const details = expanded === row.id ? parseDetails(row.details) : null;
                return (
                  <Fragment key={row.id}>
                    <tr className={row.details ? "cursor-pointer" : ""} onClick={() => row.details && setExpanded(expanded === row.id ? null : row.id)}>
                      <td className="whitespace-nowrap" title={row.count > 1 ? `Впервые: ${formatTime(row.first_at)}` : undefined}>{formatTime(row.last_at)}</td>
                      <td title={row.machine || undefined}>{row.location}{row.client_version ? <span className="text-xs text-slate-400"> v{row.client_version}</span> : null}</td>
                      <td>{CATEGORY_LABEL[row.category] || row.category}</td>
                      <td><LevelBadge level={row.level} /></td>
                      <td className="text-slate-700">
                        {row.message}
                        {row.patient_name ? <span className="text-xs text-slate-500"> · {row.patient_name}</span> : null}
                        {row.details ? <span className="text-xs text-blue-600"> · подробнее</span> : null}
                      </td>
                      <td>{row.count > 1 ? `×${row.count}` : ""}</td>
                    </tr>
                    {expanded === row.id && (
                      <tr>
                        <td colSpan={6}>
                          {(details?.documentId || details?.patientId) && (
                            <div className="flex gap-4 mb-2">
                              {details.documentId && <button className={trailButton} onClick={() => setTrailId(details.documentId)}>История обследования</button>}
                              {details.patientId && <button className={trailButton} onClick={() => setTrailId(details.patientId)}>История пациента</button>}
                            </div>
                          )}
                          <pre className="whitespace-pre-wrap break-all text-xs bg-slate-50 rounded-md p-3 text-slate-700">{formatDetails(row.details)}</pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-end gap-3 mt-3 text-sm text-slate-600">
            <span>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}</span>
            <button className="text-blue-700 disabled:text-slate-300" disabled={offset === 0} onClick={() => goToOffset(Math.max(0, offset - PAGE_SIZE))}>Назад</button>
            <button className="text-blue-700 disabled:text-slate-300" disabled={offset + PAGE_SIZE >= total} onClick={() => goToOffset(offset + PAGE_SIZE)}>Дальше</button>
          </div>
        </>
      )}

      {trailId && <TrailPanel id={trailId} onClose={() => setTrailId(null)} />}
    </div>
  );
}
